import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How to Reduce Sourcing Costs Using GTBuy Spreadsheet",
  description: "Practical, actionable methods to cut product sourcing expenses using the GTBuy structured approach. Learn price comparison tactics, bulk negotiation, and supplier tracking.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/reduce-sourcing-costs`,
  },
};

export default function ReduceSourcingCosts() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-32 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"><i className="ri-arrow-left-line"></i> Back to Home</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Strategy</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">Strategy &middot; 7 min read</span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">How to Reduce Sourcing Costs Using GTBuy Spreadsheet</h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">Practical, actionable methods to cut your product sourcing expenses using the GTBuy structured approach. Learn price comparison tactics, bulk negotiation strategies, and how to track supplier performance systematically.</p>
            <p className="text-xs text-foreground-400 mt-4">Published: July 2026</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img src="https://readdy.ai/api/search-image?query=Cost%20savings%20concept%20photography%20with%20laptop%20showing%20decreasing%20cost%20line%20chart%20on%20spreadsheet%20alongside%20piggy%20bank%20and%20stacked%20silver%20coins%20on%20bright%20white%20desk%20with%20small%20green%20money%20plant%20warm%20golden%20sunlight%20streaming%20optimistic%20financial%20planning%20scene%20high%20detail%20editorial&width=1600&height=700&seq=article-reduce-costs-header&orientation=landscape"
                alt="Reduce Sourcing Costs" title="Reduce Sourcing Costs with GTBuy Spreadsheet" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </section>

        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Why Most Sellers Overpay on Sourcing</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">The number one reason ecommerce sellers overpay for products is simple: <strong>they do not collect enough comparison data before making supplier decisions</strong>. When you only have one or two quotes per product, you have no leverage and no way to know if the price is competitive.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-8">The GTBuy Spreadsheet methodology directly attacks this problem by making it trivially easy to compare three or more supplier quotes for every single product in your catalog. Here are the concrete strategies that generate real savings.</p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">Cost Reduction Strategies &amp; Expected Savings</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tl-lg">Strategy</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tr-lg">Savings Potential</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Always get 3+ quotes per product", "10-20%"],
                    ["Negotiate based on visible competitor pricing", "5-15%"],
                    ["Consolidate orders for volume discounts", "8-18%"],
                    ["Track seasonal patterns and buy during dips", "5-12%"],
                    ["Drop underperforming suppliers quickly", "3-8%"],
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-background-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">{row[0]}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-accent-700 border-b border-background-200">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">The Four-Month Cost Reduction Plan</h2>
            <ul className="list-disc list-inside space-y-4 mb-8 text-base text-foreground-700 leading-relaxed">
              <li><strong>Month 1:</strong> Build your GTBuy master sheet with all current products and existing supplier quotes. You will likely spot overpayment opportunities immediately.</li>
              <li><strong>Month 2:</strong> Request third quotes for your top 20 products by volume. Negotiate with existing suppliers using the new comparison data.</li>
              <li><strong>Month 3:</strong> Consolidate orders where one supplier clearly outperforms others across multiple products. Bundling increases your negotiation power.</li>
              <li><strong>Month 4:</strong> Review your historical pricing data and establish target costs for each product category. Set up automated alerts when supplier pricing exceeds targets.</li>
            </ul>

            <p className="text-sm text-foreground-500 italic mb-4">
              Related strategies:{" "}
              <Link to="/improve-profit-margins" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Improve Profit Margins</Link>{" "}&middot;{" "}
              <Link to="/best-sourcing-tools" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Best Sourcing Tools</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        <section className="relative w-full py-20 px-6 md:px-10 bg-foreground-950 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Start Cutting Your Sourcing Costs</h2>
            <p className="text-foreground-400 text-sm mb-6">Explore 14 product categories on KA Spreadsheet. Apply these cost-reduction strategies to every product you source.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">GTBuy Sheet — Shop Now</a>
              <Link to="/" className="px-6 py-3 rounded-full bg-foreground-800 text-background-50 font-medium text-sm hover:bg-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Back to Homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}